import styled from "@emotion/styled";
import Image from "next/legacy/image";
import Link from "next/link";
import React from "react";
import { Typography } from "../Typography";

type Image = {
  src: string;
  alt: string;
};

type Link = {
  title: string;
  url: string;
};

export type ResourceCardProps = {
  title: string;
  description: string;
  image: Image;
  link: Link;
  label?: string;
  isLight?: boolean;
  slug?: string | string[];
};

export type OverrideSlideProps = {
  component: React.ReactElement;
};

const CardWrapper = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
  width: 327px;
  margin-right: 24px;

  &:last-child {
    margin-right: 0;
  }
  @media ${(props) => props.theme.bp.tabletUp} {
    width: 356px;
  }
`;

const ContentWrapper = styled.div<{
  isLight?: boolean;
}>`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  width: 100%;
  padding: 32px;
  background-color: ${(props) =>
    props.isLight ? props.theme.colors.snowdrop : props.theme.colors.mist};
`;

const ImageWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 224px;
  max-width: 356px;
  background-color: ${(props) =>
    props.theme.colors.opacity(props.theme.colors.snowdrop, 0.5)};

  img {
    object-fit: cover;
  }
`;

const DescriptionWrapper = styled(Typography)`
  font-size: 16px !important;
  flex-grow: 1;
`;

const LabelWrapper = styled.div`
  position: absolute;
  text-align: center;
  top: 16px;
  left: 0;
  z-index: 1;
  background-color: ${({ theme }) => theme.colors.charcoal};
  padding: 8px 13px;
  min-width: 93px;
`;

const Label = styled(Typography)`
  color: ${({ theme }) => theme.colors.snowdrop} !important;
  font-weight: bold !important;
  height: 100%;
`;

const Title = styled(Typography)`
  margin-bottom: 12px;
`;

const StyledLink = styled(Link)<{ isLight?: boolean }>`
  display: block;
  margin-top: 32px;
  color: ${({ theme }) => theme.colors.charcoal};
  font-size: 16px;
`;

export const ResourceCard = ({
  title,
  description,
  image,
  link,
  label,
  isLight,
  slug,
}: ResourceCardProps) => {
  return (
    <CardWrapper>
      {label && (
        <LabelWrapper>
          <Label use="label" as="p">
            {label}
          </Label>
        </LabelWrapper>
      )}
      <ImageWrapper>
        <Image src={image.src} alt={image.alt} layout="fill" />
      </ImageWrapper>
      <ContentWrapper isLight={isLight}>
        <Title use="componentTitle" as="h2">
          {title}
        </Title>
        <DescriptionWrapper use="body1" as="p">
          {description}
        </DescriptionWrapper>
        {link && <StyledLink href={link.url}>{link.title}</StyledLink>}
      </ContentWrapper>
    </CardWrapper>
  );
};

const White = (props: ResourceCardProps) => {
  return <ResourceCard {...props} isLight />;
};

const Override = ({ component }: OverrideSlideProps) => {
  return <CardWrapper>{component}</CardWrapper>;
};

ResourceCard.White = White;
ResourceCard.Override = Override;
